/* global axios */
import ApiClient from '../ApiClient';
import Auth from '../auth';

const campaignsClient = new ApiClient('jabvox/dialer_campaigns', {
  accountScoped: true,
});
const logsClient = new ApiClient('jabvox/dialer_call_logs', {
  accountScoped: true,
});

export const dialerCampaignsAPI = {
  getAll: () => axios.get(campaignsClient.url),
  getById: id => axios.get(`${campaignsClient.url}/${id}`),
  create: data => axios.post(campaignsClient.url, { campaign: data }),
  update: (id, data) =>
    axios.patch(`${campaignsClient.url}/${id}`, { campaign: data }),
  destroy: id => axios.delete(`${campaignsClient.url}/${id}`),
  start: id => axios.patch(`${campaignsClient.url}/${id}/start`),
  pause: id => axios.patch(`${campaignsClient.url}/${id}/pause`),
  stop: id => axios.patch(`${campaignsClient.url}/${id}/stop`),
  retryContacts: id =>
    axios.patch(`${campaignsClient.url}/${id}/retry_contacts`),
  getContacts: id => axios.get(`${campaignsClient.url}/${id}/contacts`),
  getCallLogs: id => axios.get(`${campaignsClient.url}/${id}/call_logs`),
  importContacts: (id, contacts) =>
    axios.post(`${campaignsClient.url}/${id}/import_contacts`, { contacts }),
  originate: (id, data) =>
    axios.post(`${campaignsClient.url}/${id}/originate`, data),
  leadCount: params =>
    axios.get(`${campaignsClient.url}/lead_count`, { params }),
  matchingLeadIds: params =>
    axios.get(`${campaignsClient.url}/matching_lead_ids`, { params }),
  previewLeads: params =>
    axios.get(`${campaignsClient.url}/preview_leads`, { params }),
  getLeadFields: () => axios.get(`${campaignsClient.url}/lead_fields`),
  // Server-side value search for custom fields with more values than the cap.
  searchFieldValues: (key, q) =>
    axios.get(`${campaignsClient.url}/field_values`, { params: { key, q } }),
  getReport: (id, params) =>
    axios.get(`${campaignsClient.url}/${id}/report`, { params }),
  getProgress: id => axios.get(`${campaignsClient.url}/${id}/progress`),
  // Live monitor: agentes conectados a la campaña + llamadas en curso (poll ~3s).
  liveMonitor: id => axios.get(`${campaignsClient.url}/${id}/live_monitor`),
  redialLead: (id, data) =>
    axios.patch(`${campaignsClient.url}/${id}/redial_lead`, data),
  excludeLead: (id, data) =>
    axios.patch(`${campaignsClient.url}/${id}/exclude_lead`, data),
};

export const dialerCallLogsAPI = {
  getAll: () => axios.get(logsClient.url),
  create: data => axios.post(logsClient.url, data),
  // Save the agent's wrap-up typification + note for a contact's most recent call.
  typify: data => axios.patch(`${logsClient.url}/typify`, data),
};

const dialerStatesClient = new ApiClient('jabvox/dialer_states', {
  accountScoped: true,
});
const dialerAccessesClient = new ApiClient('jabvox/dialer_accesses', {
  accountScoped: true,
});

export const dialerStatesAPI = {
  getAll: () => axios.get(dialerStatesClient.url),
  create: data => axios.post(dialerStatesClient.url, data),
  update: (id, data) => axios.patch(`${dialerStatesClient.url}/${id}`, data),
  destroy: id => axios.delete(`${dialerStatesClient.url}/${id}`),
};

export const dialerAccessesAPI = {
  getAll: () => axios.get(dialerAccessesClient.url),
  update: (userId, canAccess) =>
    axios.patch(`${dialerAccessesClient.url}/${userId}`, {
      can_access: canAccess,
    }),
  me: () => axios.get(`${dialerAccessesClient.url}/me`),
  connect: campaignId =>
    axios.post(`${dialerAccessesClient.url}/connect`, {
      campaign_id: campaignId,
    }),
  disconnect: () => axios.delete(`${dialerAccessesClient.url}/connect`),
  // Desconexión apta para 'beforeunload' (cierre de pestaña/navegador): fetch con
  // keepalive sobrevive al cierre de la página, cosa que axios no. Lleva los
  // headers de auth manualmente (sendBeacon no puede). Best-effort: si falla, el
  // reaper del servidor saca al agente de la cola en <=1 min de todos modos.
  disconnectBeacon: () => {
    try {
      const { apiHost = '' } = window.jabvoxConfig || {};
      const auth = Auth.hasAuthCookie() ? Auth.getAuthData() : {};
      return fetch(`${apiHost}/${dialerAccessesClient.url}/connect`, {
        method: 'DELETE',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'access-token': auth['access-token'] || '',
          'token-type': auth['token-type'] || '',
          client: auth.client || '',
          expiry: auth.expiry || '',
          uid: auth.uid || '',
        },
      });
    } catch {
      return Promise.resolve();
    }
  },
  updateState: stateId =>
    axios.patch(`${dialerAccessesClient.url}/state`, { state_id: stateId }),
  // Marca/desmarca la gestión post-llamada (wrap-up) para el monitor en vivo.
  setWrapup: active =>
    axios.patch(`${dialerAccessesClient.url}/wrapup`, { active }),
  heartbeat: () => axios.post(`${dialerAccessesClient.url}/heartbeat`),
  requestCall: () => axios.post(`${dialerAccessesClient.url}/request_call`),
  endCall: dialerContactId =>
    axios.patch(`${dialerAccessesClient.url}/end_call`, {
      dialer_contact_id: dialerContactId,
    }),
};
